// Client-side favorites & recently-viewed history via localStorage,
// with optional Supabase cloud sync for signed-in users.
import { useEffect, useState, useCallback } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const FAV_KEY = "eidrolon:favorites";
const HIST_KEY = "eidrolon:history";
const HIST_MAX = 20;

function read(key: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch { return []; }
}

function write(key: string, list: string[]) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(key, JSON.stringify(list)); } catch {}
  window.dispatchEvent(new CustomEvent("eidrolon-storage", { detail: { key } }));
}

function sameList(a: string[], b: string[]) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function useList(key: string): [string[], (next: string[]) => void] {
  const [list, setList] = useState<string[]>([]);
  useEffect(() => {
    setList(read(key));
    const on = (e: Event) => {
      const ce = e as CustomEvent<{ key: string }>;
      if (ce.detail?.key === key) setList(read(key));
    };
    const onStorage = () => setList(read(key));
    window.addEventListener("eidrolon-storage", on);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("eidrolon-storage", on);
      window.removeEventListener("storage", onStorage);
    };
  }, [key]);
  const setter = useCallback((next: string[]) => {
    const current = read(key);
    if (sameList(current, next)) {
      setList((existing) => sameList(existing, next) ? existing : next);
      return;
    }
    write(key, next);
    setList(next);
  }, [key]);
  return [list, setter];
}

export function useFavorites() {
  const [favs, setFavs] = useList(FAV_KEY);
  const isFav = useCallback((slug: string) => favs.includes(slug), [favs]);
  const toggle = useCallback((slug: string) => {
    setFavs(favs.includes(slug) ? favs.filter((s) => s !== slug) : [slug, ...favs]);
  }, [favs, setFavs]);
  const clear = useCallback(() => setFavs([]), [setFavs]);
  return { favorites: favs, isFavorite: isFav, toggleFavorite: toggle, clearFavorites: clear };
}

export function useHistory() {
  const [hist, setHist] = useList(HIST_KEY);
  const push = useCallback((slug: string) => {
    const current = read(HIST_KEY);
    const next = [slug, ...current.filter((s) => s !== slug)].slice(0, HIST_MAX);
    setHist(next);
  }, [setHist]);
  const clear = useCallback(() => setHist([]), [setHist]);
  return { history: hist, pushHistory: push, clearHistory: clear };
}

export function useTheme() {
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  useEffect(() => {
    const saved = (typeof window !== "undefined" ? window.localStorage.getItem("eidrolon:theme") : null) as "dark" | "light" | null;
    const initial: "dark" | "light" = saved ?? "dark";
    setTheme(initial);
    document.documentElement.classList.toggle("light", initial === "light");
    document.documentElement.classList.toggle("dark", initial === "dark");
  }, []);
  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try { window.localStorage.setItem("eidrolon:theme", next); } catch {}
      document.documentElement.classList.toggle("light", next === "light");
      document.documentElement.classList.toggle("dark", next === "dark");
      return next;
    });
  }, []);
  return { theme, toggleTheme: toggle };
}

// ---------------------------------------------------------------
// Auth session hook + cloud sync
// ---------------------------------------------------------------

const BUCKET_KEYS = {
  favorites: FAV_KEY,
  history: HIST_KEY,
  decks: "palstars:decks",
  tracker: "palstars.tcgTracker",
} as const;
export type BucketKey = keyof typeof BUCKET_KEYS;

export function useSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => setSession(s));
    return () => { alive = false; sub.subscription.unsubscribe(); };
  }, []);
  return { session, loading, user: session?.user ?? null };
}

async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user.id ?? null;
}

export async function syncBucketFromLocal(key: BucketKey): Promise<void> {
  const uid = await currentUserId();
  if (!uid) return;
  const storageKey = BUCKET_KEYS[key];
  let raw: unknown = null;
  try { const s = localStorage.getItem(storageKey); raw = s ? JSON.parse(s) : null; } catch { raw = null; }
  const payload = raw ?? (key === "tracker" ? [] : []);
  const { error } = await supabase.from("user_data").upsert(
    { user_id: uid, key, data: payload as never, updated_at: new Date().toISOString() },
    { onConflict: "user_id,key" },
  );
  if (error) throw error;
}

export async function pullBucketToLocal(key: BucketKey): Promise<void> {
  const uid = await currentUserId();
  if (!uid) return;
  const { data, error } = await supabase.from("user_data").select("data").eq("user_id", uid).eq("key", key).maybeSingle();
  if (error) throw error;
  if (!data) return;
  const storageKey = BUCKET_KEYS[key];
  try {
    localStorage.setItem(storageKey, JSON.stringify(data.data ?? (key === "tracker" ? [] : [])));
    window.dispatchEvent(new CustomEvent("eidrolon-storage", { detail: { key: storageKey } }));
  } catch {}
}

